/**
 * Addy-internal / test organization detection, for the "exclude Addy-owned
 * orgs" toggle on the cost breakdown.
 *
 * This MIRRORS the backend heuristic in
 * `backend/functions/services/pcInactivity/orgEligibility.js:91`
 * (`isTestOrInternalOrg`) — deliberately, so the two agree on who counts as a
 * real paying customer.
 *
 * IMPORTANT LIMITATION: the backend also matches on the org OWNER'S EMAIL
 * (`isAddyEmail`, plus-alias QA accounts, and personal-domain addresses
 * containing "addyai"/"addy-ai"/"addytest"). The observability endpoint this
 * page reads gives us only an org id and, via the org-names lookup, a display
 * name — no owner email. So this is the ID + NAME subset of the backend rule
 * and will under-detect internal orgs whose name looks like a normal company
 * but whose owner is on an Addy address.
 *
 * Practical consequence: excluding internal orgs here is a FLOOR, not a
 * guarantee. Treat "customer cost" with the toggle on as an upper bound.
 */

/**
 * Internal/test orgs whose names the patterns below do not catch — Addy team
 * accounts on personal domains. Mirrors `KNOWN_TEST_ORG_IDS` at
 * orgEligibility.js:17-22 and the duplicate in
 * `scripts/listReferralEligibleCustomerOrgs.js:36`.
 *
 * If the backend list changes, change it here too — there is no shared source.
 */
const KNOWN_TEST_ORG_IDS = new Set([
  "bBuZCTkkwrpV9RUv6mEk",
  "e3paC6nYOFe4CvXI1V4z",
  "F555kwDq5Zrzqc9Xv3q7",
  "AZb5XbMcMe2AaZgfuEqs",
]);

// `\btest\b` and `\baddy\b` are word-bounded on purpose: an unanchored /addy/
// would swallow real customers like "Maddy Lending".
const TEST_ORG_NAME_PATTERN = /\btest\b/i;
const INTERNAL_ADDY_NAME_PATTERN = /\baddy\b/i;

/**
 * @param {string} orgId
 * @param {string} [orgName] resolved display name, when we have one
 * @returns {boolean} true when this org looks Addy-internal or like a test org
 */
export const isInternalOrg = (orgId, orgName) => {
  if (orgId && KNOWN_TEST_ORG_IDS.has(orgId)) return true;

  // Fall back to the id when no name resolved: an org literally named
  // "…test…" and an unresolvable id are different cases, and only the former
  // should match, so we do NOT pattern-match a bare id here.
  const name = typeof orgName === "string" ? orgName.trim() : "";
  if (!name) return false;

  return (
    TEST_ORG_NAME_PATTERN.test(name) || INTERNAL_ADDY_NAME_PATTERN.test(name)
  );
};

/**
 * Why a given org was classified internal — shown in a tooltip so the number
 * is auditable rather than a black box.
 * @returns {string|null}
 */
export const internalOrgReason = (orgId, orgName) => {
  if (orgId && KNOWN_TEST_ORG_IDS.has(orgId)) {
    return "Known internal/test org ID (hardcoded allowlist)";
  }
  const name = typeof orgName === "string" ? orgName.trim() : "";
  if (!name) return null;
  if (TEST_ORG_NAME_PATTERN.test(name)) return 'Name contains "test"';
  if (INTERNAL_ADDY_NAME_PATTERN.test(name)) return 'Name contains "addy"';
  return null;
};
